import { Dispatch, ReactNode, SetStateAction } from "react";
import { SxProps, Theme } from "@mui/material";
import {
  Feature,
  FeatureCollection,
  Geometry,
  Position,
  Polygon,
  Point,
} from "geojson";
import { bufferOptions } from "../../common/form-components/risk-monitoring/InputOptions";

export const incidentFilterSelectedColour = "#40e4ab";

// *~*~*~*~*~*~*~*~*~*~*~*~*~*~*~*~ incident types ~~*~*~*~*~*~*~*~*~*~*~*~*~*~*~*~*

export type PotentialBufferValues = (typeof bufferOptions)[number];

export type CellValue = number | undefined | "N/A";

export type IncidentType =
  | "FIRE"
  | "DROUGHT"
  | "DEFORESTATION"
  | "FLOOD"
  | "EARTHQUAKE"
  | "TROPICAL CYCLONE"
  | "VOLCANO"
  | "TSUNAMI";

export type ApiRef = "FIRMS" | "GFW" | "GDACS";
export type IncidentTableData = {
  incidentsId: string;
  scannedAt: number;
  boundaryBufferKm?: PotentialBufferValues;
  type: IncidentType;
  apiRef: ApiRef;
  incidentSpecificTime: number;
  incidentCount?: number;
  areaM2: number;
  incidentFileShapes: IncidentFeatureCollection;
};

export type RowProps = {
  row: IncidentTableData;
  solutionId: string;
  selectedIncident: IncidentTableData | null;
  selectedIncidents: Array<IncidentTableData>;
  setSelectedIncident: Dispatch<SetStateAction<IncidentTableData | null>>;
  setSelectedIncidents: Dispatch<SetStateAction<Array<IncidentTableData>>>;
};

export type BufferedSolutionShapes = {
  type: "Feature";
  geometry: {
    type: "Polygon";
    coordinates: Position[][];
  };
  properties: {
    buffer: number;
    createdAt: number;
    [key: string]: string | number;
  };
};

export type ProjectIncidentData = {
  tableData: Array<IncidentTableData>;
  outline: Array<Feature> | undefined;
  bufferArea: Array<BufferedSolutionShapes>;
  initialShapeCreatedAt?: number;
};

export type GroupedIncidentFeatures = Partial<
  Record<IncidentType, Array<IncidentFeature>>
>;

export const incidentTypes = [
  "FIRE",
  "DEFORESTATION",
  "FLOOD",
  "EARTHQUAKE",
  "TROPICAL CYCLONE",
  "TSUNAMI",
  "VOLCANO",
  "DROUGHT",
] as const;

export type IncidentFeature = Feature<Geometry, IncidentCardProperties>;
export type IncidentFeatureCollection = FeatureCollection<
  Geometry,
  IncidentCardProperties
>;

export type FillColours = string;

export type FillStyles = {
  id: string;
  colour: FillColours;
  opacity: number;
  background: string;
};

export type IncidentStyleMap = Record<IncidentType, FillStyles>;
export type BaseStyleKey = "outline" | "buffer" | "FallbackOption";
export type BaseStyleMap = Record<BaseStyleKey, FillStyles>;
export type StyleMap = IncidentStyleMap & BaseStyleMap;

// *~*~*~*~*~*~*~*~*~*~*~*~*~*~*~*~ style types ~~*~*~*~*~*~*~*~*~*~*~*~*~*~*~*~*

export type MuiAlignStyle = "center" | "inherit" | "left" | "right" | "justify";
export type Category = "area" | "type" | "time";

export type IncidentInfo = {
  type: IncidentType;
  category: Category;
};

// *~*~*~*~*~*~*~*~*~*~*~*~*~*~*~*~ sort types ~~*~*~*~*~*~*~*~*~*~*~*~*~*~*~*~*

export type IncidentTableSortKey =
  | "scannedAt"
  | "type"
  | "incidentSpecificTime"
  | "boundaryBufferKm"
  | "areaM2";

export type IncidentTableSortDirection = "asc" | "desc";

export type IncidentTableSortState = {
  key: IncidentTableSortKey;
  direction: IncidentTableSortDirection;
};

// *~*~*~*~*~*~*~*~*~*~*~*~*~*~*~*~ filter types ~~*~*~*~*~*~*~*~*~*~*~*~*~*~*~*~*

export type IncidentDateFilterField = "scannedAt" | "incidentSpecificTime";
export type IncidentAreaFilterOperator =
  | "greaterThan"
  | "fewerThan"
  | "equalTo";

export type IncidentTableFilterState = {
  dateField: IncidentDateFilterField;
  dateFrom: number | null;
  dateTo: number | null;
  type: string;
  boundaryBufferKm: string;
  areaOperator: IncidentAreaFilterOperator;
  areaM2: string;
};

export type IncidentTableFilterUpdateValue<
  FilterKey extends keyof IncidentTableFilterState,
> = IncidentTableFilterState[FilterKey];

export type IncidentDatePickerFilterKey = "dateFrom" | "dateTo";

export type IncidentDatePickerProps = {
  label: string;
  value: number | null;
  filterKey: IncidentDatePickerFilterKey;
  onChange: (key: IncidentDatePickerFilterKey, value: Date | null) => void;
};

export type IncidentDateFieldSwitchProps = {
  value: IncidentDateFilterField;
  onChange: (value: IncidentDateFilterField) => void;
};

export type IncidentTableStyles = {
  filterTextFieldSx: SxProps<Theme>;
  boundaryTextFieldSx: SxProps<Theme>;
  areaTextFieldSx: SxProps<Theme>;
  filterMenuItemSx: SxProps<Theme>;
  datePickerTextFieldSx: SxProps<Theme>;
  datePickerDaySx: SxProps<Theme>;
  filterToolbarSx: SxProps<Theme>;
  filterToolbarContentSx: SxProps<Theme>;
  resetFiltersButtonSx: SxProps<Theme>;
  dateFieldSwitchContainerSx: SxProps<Theme>;
  datePickerContainerSx: SxProps<Theme>;
  areaOperatorSelectSx: SxProps<Theme>;
};

// *~*~*~*~*~*~*~*~*~*~*~*~*~*~*~*~ table types ~~*~*~*~*~*~*~*~*~*~*~*~*~*~*~*~*

export type TableCellProps = {
  name: ReactNode;
  tooltip?: string;
  align: MuiAlignStyle;
  incidentInfo?: IncidentInfo;
};

export type UseIncidentTableFiltersParams = {
  rows: Array<IncidentTableData>;
  setPage: Dispatch<SetStateAction<number>>;
};

export type UseIncidentTableSortParams = {
  rows: Array<IncidentTableData>;
};

export type UseIncidentTablePaginationParams = {
  rows: Array<IncidentTableData>;
  page: number;
  rowsPerPage: number;
};

export type ReadOnlyRiskIncidentDataProps = {
  solutionId: string;
  page: number;
  rowsPerPage: number;
  setPage: Dispatch<SetStateAction<number>>;
  rows: Array<IncidentTableData>;
  selectedIncident: IncidentTableData | null;
  selectedIncidents: Array<IncidentTableData>;
  setSelectedIncident: Dispatch<SetStateAction<IncidentTableData | null>>;
  setSelectedIncidents: Dispatch<SetStateAction<Array<IncidentTableData>>>;
};

// information card types

export type IncidentInformationCardProps = {
  properties: Record<string, unknown> | null;
  type: IncidentType | "buffer" | "outline" | null;
  apiRef: ApiRef | null;
};

export type HoveredFeature = {
  properties: Record<string, unknown> | null;
  geometry?: Geometry | null;
  type: IncidentType | "buffer" | "outline" | null;
  apiRef: ApiRef | null;
};

export type IncidentDetailsValueProps = {
  value: unknown;
  nestedKey?: string;
};

export type IncidentCardContainerProps = {
  children: ReactNode;
  borderColour: string;
  backgroundColor: string;
  pointerEvents?: "auto" | "none";
};

// *~*~*~*~*~*~*~*~*~*~*~*~*~*~*~*~ incident property types ~~*~*~*~*~*~*~*~*~*~*~*~*~*~*~*~*

export type Confidence = "L" | "N" | "H";
export type DayNight = "D" | "N";
export type GDACSEventTypes = "FL" | "EQ" | "TC" | "TS" | "VO" | "DR";
export type GDACSAlertLevel = "Green" | "Orange" | "Red";

export type FireDataProperties = {
  centroid: {
    lat: number;
    lon: number;
  };
  pixel: {
    scan: number;
    track: number;
  };
  acquisitionDetails: {
    overPassTimeUTC: number;
    satellite?: "NOAA-21-NearRealTime";
    instrument?: string;
  };
  confidence: Confidence;
  collection: string;
  source: string;
  strength?: {
    ti4Brightness?: number;
    ti5Brightness?: number;
    radiativePower?: number;
  };
  dayNight: DayNight;
};

export type DeforestationProperties = {
  centroid: {
    lat: number;
    lon: number;
  };
  acquisitionDetails: {
    dateUTC: string;
    satellite?: "aggregated";
    instrument?: "aggregated";
  };
  confidence: "XH";
  collection: "gfw_integrated_alerts/latest";
  source: "GFW_INTEGRATED_ALERTS";
  intensity?: number;
  totalAreaHa: number;
};

export type GdacsProperties = {
  polygonCreationMethod?: "severity-area" | "bbox" | "default-buffer";
  eventType: GDACSEventTypes;
  eventId: number;
  episodeId: number;
  eventName: string;
  uniqueGlideId: string;
  webDescriptiveName: string;
  description: string;
  htmlFormattedDescription: string;
  icon: string;
  iconOverall: string;
  url: {
    geometry: string;
    report: string;
    details: string;
  };
  alertLevel: GDACSAlertLevel;
  alertScore: number;
  episodeAlertLevel: GDACSAlertLevel;
  episodeAlertScore: number;
  isTemporary: boolean;
  isCurrent: boolean;
  firstRecorded: number;
  lastRecorded: number;
  dateModified: number;
  source: string;
  sourceId: string;
  polygonDate: string;
  polygonLabel: string;
  affectedCountries: Array<string>;
  severityData: {
    severity: number;
    severityText: string;
    severityUnit: string;
  };
  class: string;
};

export type SharedIncidentProperties = {
  incidentId: string;
  startTime: EpochTimeStamp;
  currentTime: EpochTimeStamp;
  incident: IncidentType;
  ongoing: boolean;
  buffer: PotentialBufferValues;
  centroid: {
    lat: number;
    lon: number;
  };
};

export type IncidentCardLink = {
  label: string;
  href: string;
};

export type IncidentCardData = {
  title: string;
  titleColour: string;
  paragraphs: Array<string>;
  links: Array<IncidentCardLink>;
};

export type FireIncidentCardProperties = SharedIncidentProperties &
  FireDataProperties;
export type DeforestationIncidentCardProperties = SharedIncidentProperties &
  DeforestationProperties;
export type GdacsIncidentCardProperties = SharedIncidentProperties &
  GdacsProperties;
export type IncidentCardProperties =
  | FireIncidentCardProperties
  | DeforestationIncidentCardProperties
  | GdacsIncidentCardProperties;

export type IncidentFeaturePropertiesWithApiRef = IncidentCardProperties & {
  apiRef?: ApiRef;
};

// *~*~*~*~*~*~*~*~*~*~*~*~*~*~*~*~ map types ~~*~*~*~*~*~*~*~*~*~*~*~*~*~*~*~*

export type MapComponentProps = {
  outline: Array<Feature<Polygon>> | undefined;
  bufferArea: Array<BufferedSolutionShapes> | undefined;
  selectedIncident: IncidentTableData | null;
  selectedIncidents: Array<IncidentTableData>;
};

export type IncidentMarkerProperties = IncidentFeaturePropertiesWithApiRef & {
  incidentFeatureIndex: number;
};

export type IncidentMarkerCollection = FeatureCollection<
  Point,
  IncidentMarkerProperties
>;

export type UseMapViewportParams = {
  outline: Array<Feature<Polygon>> | undefined;
  bufferArea: Array<BufferedSolutionShapes> | undefined;
};

export type UseSelectedIncidentBoundsParams = {
  bufferArea: Array<BufferedSolutionShapes> | undefined;
  selectedIncidentShapes: IncidentTableData["incidentFileShapes"] | undefined;
};

export type UseIncidentMarkersParams = {
  visibleIncidentCollection: IncidentFeatureCollection | null;
};

export type UseInteractiveFeaturesResult = {
  hoveredFeature: HoveredFeature;
  clickedFeature: HoveredFeature;
  hoveredFeatureCollection: FeatureCollection | null;
  clickedFeatureCollection: FeatureCollection | null;
  setHoveredFeature: Dispatch<SetStateAction<HoveredFeature>>;
  setClickedFeature: Dispatch<SetStateAction<HoveredFeature>>;
  clearHoveredFeature: () => void;
};
